import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  NavigationModule,
  getDockNavModules,
} from '../../Components/Navigation/navigationConfig';
import { canViewJob, canViewScheduler } from '../../utils/permissions';
import { useProfile } from '../../Components/Hooks/UserHooks';
import { getTenantById } from '../../services/tenantService';
import { Tenant } from '../../interfaces/Tenant';
import TopDockNavItem from './TopDockNavItem';
import TopDockOffcanvas from './TopDockOffcanvas';

/**
 * Get modules to display in the dock.
 * Visibility (which modules + their order) is the SHARED primary-nav source in
 * navigationConfig.ts (getDockNavModules) — also used by the mobile off-canvas,
 * so both navs stay in sync. To hide/show a module edit HIDDEN_NAV_MODULE_IDS
 * there. Below we only layer the DYNAMIC filters the mobile menu can't apply:
 *  - permission-based filtering for the Job module
 *  - tenant-based filtering for the Scheduler and Planner modules
 */
const getDockModules = (
  userProfileId: string | null | undefined,
  tenant: Tenant | null
): NavigationModule[] => {
  const modules = getDockNavModules();

  // Debug: Log all modules and check if Job is found
  if (process.env.NODE_ENV === 'development') {
    console.log('[TopDockNav] All dock modules before filtering:', {
      foundModules: modules.map(m => ({ id: m.id, label: m.label })),
      userProfileId,
      userProfile: userProfileId ? 'present' : 'missing'
    });
  }
  
  // Apply permission filtering for Job and Scheduler modules
  // TEMPORARILY: Show Job to all users to verify it appears
  // TODO: Restore permission check after verification
  const filteredModules = modules.filter((module) => {
    if (module.id === 'job-management') {
      const hasPermission = canViewJob(userProfileId);
      
      // Debug logging
      if (process.env.NODE_ENV === 'development') {
        console.log('[TopDockNav] Job module permission check:', {
          moduleId: module.id,
          userProfileId,
          hasPermission,
          showingJob: true, // Temporarily always true
          allowedProfileIds: [
            '9FB0E270-A6B1-4612-B708-A5CAEDC88BB3', // Admin
            'E6F7A8B9-C0D1-4234-F567-89ABCDEF0123', // Supervisor
            'F7A8B9C0-D1E2-4345-A678-9ABCDEF01234', // ProjectManager
            'A8B9C0D1-E2F3-4456-B789-ABCDEF012345'  // SiteManager
          ]
        });
      }
      // TEMPORARY: Always show Job to verify it appears
      return true; // TODO: Change back to: return hasPermission;
    }
    if (module.id === 'scheduler') {
      const hasPermission = canViewScheduler(userProfileId);
      
      // Check tenant setting: Hide if EnableLegacyScheduler is false
      const isTenantEnabled = tenant?.enableLegacyScheduler !== false;
      
      // Debug logging
      if (process.env.NODE_ENV === 'development') {
        console.log('[TopDockNav] Scheduler module permission check:', {
          moduleId: module.id,
          userProfileId,
          hasPermission,
          enableLegacyScheduler: tenant?.enableLegacyScheduler,
          isTenantEnabled,
          userProfile: userProfileId ? 'present' : 'missing',
          allowedProfileIds: [
            '9FB0E270-A6B1-4612-B708-A5CAEDC88BB3', // Admin
            'E6F7A8B9-C0D1-4234-F567-89ABCDEF0123', // Supervisor
            'F7A8B9C0-D1E2-4345-A678-9ABCDEF01234', // ProjectManager
            'A8B9C0D1-E2F3-4456-B789-ABCDEF012345'  // SiteManager
          ]
        });
      }
      // Hide Scheduler if tenant setting is false OR user doesn't have permission
      return hasPermission && isTenantEnabled;
    }
    if (module.id === 'planner') {
      // Check tenant setting: Hide if EnableDailyPlanner is false
      const isTenantEnabled = tenant?.enableDailyPlanner !== false;
      
      // Debug logging
      if (process.env.NODE_ENV === 'development') {
        console.log('[TopDockNav] Planner module tenant check:', {
          moduleId: module.id,
          enableDailyPlanner: tenant?.enableDailyPlanner,
          isTenantEnabled
        });
      }
      // Hide Planner if tenant setting is false
      return isTenantEnabled;
    }
    return true;
  });
  
  if (process.env.NODE_ENV === 'development') {
    console.log('[TopDockNav] Filtered modules:', {
      totalModules: filteredModules.length,
      moduleIds: filteredModules.map(m => m.id),
      hasJob: filteredModules.some(m => m.id === 'job-management'),
      hasScheduler: filteredModules.some(m => m.id === 'scheduler'),
      userProfileId: userProfileId,
      canViewScheduler: canViewScheduler(userProfileId)
    });
  }
  
  return filteredModules;
};

const TopDockNav: React.FC = () => {
  const location = useLocation();
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  
  // Get user profile for permission checks - use useProfile hook which handles Redux hydration fallback
  const { userProfile } = useProfile();
  // Try multiple possible field names for profileId
  const userProfileId = userProfile?.profileId || userProfile?.ProfileId || userProfile?.profileID || null;
  const tenantId = userProfile?.tenantId || userProfile?.TenantId || userProfile?.tenantID || null;
  
  // Fetch tenant data when tenantId is available
  useEffect(() => {
    if (tenantId) {
      getTenantById(tenantId)
        .then((tenantData) => {
          setTenant(tenantData);
          if (process.env.NODE_ENV === 'development') {
            console.log('[TopDockNav] Tenant data loaded:', {
              tenantId,
              enableLegacyScheduler: tenantData.enableLegacyScheduler,
              enableDailyPlanner: tenantData.enableDailyPlanner
            });
          }
        })
        .catch((error) => {
          if (process.env.NODE_ENV === 'development') {
            console.error('[TopDockNav] Error fetching tenant data:', error);
          }
          // Set tenant to null on error - modules will be shown by default (fail-open)
          setTenant(null);
        });
    }
  }, [tenantId]);
  
  // Debug logging
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[TopDockNav] User profile check:', {
        hasUser: !!userProfile,
        profileId: userProfileId,
        profileIdType: typeof userProfileId,
        canViewJob: canViewJob(userProfileId),
        canViewScheduler: canViewScheduler(userProfileId),
        userProfileKeys: userProfile ? Object.keys(userProfile) : [],
        fullUserProfile: userProfile, // Log full object for debugging
        adminProfileId: '9FB0E270-A6B1-4612-B708-A5CAEDC88BB3',
        profileIdMatchesAdmin: userProfileId?.toUpperCase() === '9FB0E270-A6B1-4612-B708-A5CAEDC88BB3'.toUpperCase()
      });
    }
  }, [userProfile, userProfileId]);
  
  // Get dock modules with permission filtering and tenant-based filtering
  const allDockModules = useMemo(() => {
    const modules = getDockModules(userProfileId, tenant);
    if (process.env.NODE_ENV === 'development') {
      console.log('[TopDockNav] Dock modules:', {
        totalModules: modules.length,
        moduleIds: modules.map(m => m.id),
        hasJob: modules.some(m => m.id === 'job-management'),
        hasScheduler: modules.some(m => m.id === 'scheduler'),
        hasPlanner: modules.some(m => m.id === 'planner'),
        tenantEnabled: {
          enableLegacyScheduler: tenant?.enableLegacyScheduler,
          enableDailyPlanner: tenant?.enableDailyPlanner
        }
      });
    }
    return modules;
  }, [userProfileId, tenant]);
  
  const [visibleModules, setVisibleModules] = useState<NavigationModule[]>(allDockModules);
  const [overflowModules, setOverflowModules] = useState<NavigationModule[]>([]);
  const navRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLDivElement>(null);

  // Check if route is active
  const isActive = (route: string): boolean => {
    if (route === '/dashboard' || route === '/dashboard-modern' || route === '/admin-dashboard') {
      return location.pathname === route;
    }
    // Job route includes /clients/jobs routes
    if (route === '/clients/jobs') {
      return location.pathname === route || 
             location.pathname.startsWith(route + '/');
    }
    // Scheduler route (top-level)
    if (route === '/scheduler') {
      return location.pathname === route || 
             location.pathname.startsWith(route + '/');
    }
    // Planner route (top-level) — now opens the Dispatch Board
    if (route === '/planner') {
      return location.pathname === route || 
             location.pathname.startsWith(route + '/');
    }
    // Special case: Client Management includes both /clients and /meetings routes
    // But exclude work orders which belong to Scaffold, and exclude jobs which have their own nav
    if (route === '/clients') {
      return (location.pathname === route || 
             location.pathname.startsWith(route + '/') || 
             location.pathname.startsWith('/meetings')) &&
             !location.pathname.includes('/clients/work-orders') &&
             !location.pathname.includes('/clients/jobs');
    }
    // Scaffold route includes work orders and scaffolds (plural) routes
    // But exclude scheduler which has its own top-level nav
    if (route === '/scaffold') {
      return (location.pathname === route || 
             location.pathname.startsWith(route + '/') ||
             location.pathname.startsWith('/scaffolds') ||
             location.pathname.includes('/clients/work-orders')) &&
             !location.pathname.startsWith('/scheduler');
    }
    // Inventory route includes /inventory, /products, /stock-items, /purchase-orders, /suppliers
    if (route === '/inventory') {
      return location.pathname === route || 
             location.pathname.startsWith(route + '/') ||
             location.pathname.startsWith('/products') ||
             location.pathname.startsWith('/stock-items') ||
             location.pathname.startsWith('/purchase-orders') ||
             location.pathname.startsWith('/suppliers');
    }
    // Settings route includes /settings, /account/departments, /account/tenant-roles, /account/global-users, /tenant-locations, /assets, /tenant-labour-rates
    if (route === '/settings') {
      return location.pathname === route || 
             location.pathname.startsWith(route + '/') ||
             location.pathname.startsWith('/account/departments') ||
             location.pathname.startsWith('/account/tenant-roles') ||
             location.pathname.startsWith('/account/global-users') ||
             location.pathname.startsWith('/tenant-locations') ||
             location.pathname.startsWith('/assets') ||
             location.pathname.startsWith('/tenant-labour-rates');
    }
    return location.pathname === route || location.pathname.startsWith(route + '/');
  };

  // Handle overflow detection and module visibility
  useEffect(() => {
    const checkOverflow = () => {
      if (!navRef.current || !itemsRef.current) return;

      const navWidth = navRef.current.offsetWidth;
      const items = Array.from(itemsRef.current.children) as HTMLElement[];
      const padding = 32; // Total horizontal padding
      const moreButtonWidth = 64; // Approximate width of "More" button
      let totalWidth = 0;
      const visible: NavigationModule[] = [];
      const overflow: NavigationModule[] = [];

      // Check each module to see if it fits
      for (let i = 0; i < allDockModules.length; i++) {
        const module = allDockModules[i];
        const item = items[i];
        
        let itemWidth: number;
        if (item && item.offsetWidth > 0) {
          itemWidth = item.offsetWidth;
        } else {
          // Estimate width if item not rendered yet
          itemWidth = window.innerWidth < 768 ? 56 : 80;
        }

        // Check if this item would fit (including "More" button if needed)
        const wouldNeedMoreButton = i < allDockModules.length - 1; // Need more button if not last item
        const requiredWidth = totalWidth + itemWidth + (wouldNeedMoreButton ? moreButtonWidth : 0) + padding;

        if (requiredWidth <= navWidth) {
          visible.push(module);
          totalWidth += itemWidth;
        } else {
          // This and all remaining modules go to overflow
          overflow.push(...allDockModules.slice(i));
          break;
        }
      }

      // If all fit, show all
      if (overflow.length === 0) {
        setVisibleModules(allDockModules);
        setOverflowModules([]);
      } else {
        setVisibleModules(visible);
        setOverflowModules(overflow);
      }
    };

    // Initial check after a delay to ensure DOM is ready
    const timeoutId = setTimeout(checkOverflow, 150);
    window.addEventListener('resize', checkOverflow);

    return () => {
      window.removeEventListener('resize', checkOverflow);
      clearTimeout(timeoutId);
    };
  }, [allDockModules, location.pathname]);
  
  // Update visible modules when dock modules change (e.g., permission changes)
  useEffect(() => {
    setVisibleModules(allDockModules);
    setOverflowModules([]);
  }, [allDockModules]);

  // Keyboard navigation handler
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const items = Array.from(itemsRef.current?.children || []) as HTMLElement[];
    const currentIndex = items.findIndex((item) => item === document.activeElement);

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        if (currentIndex > 0) {
          items[currentIndex - 1]?.focus();
        }
        break;
      case 'ArrowRight':
        e.preventDefault();
        if (currentIndex < items.length - 1) {
          items[currentIndex + 1]?.focus();
        }
        break;
      case 'Home':
        e.preventDefault();
        items[0]?.focus();
        break;
      case 'End':
        e.preventDefault();
        items[items.length - 1]?.focus();
        break;
    }
  };

  const toggleOffcanvas = () => {
    setOffcanvasOpen(!offcanvasOpen);
  };

  return (
    <nav
      ref={navRef}
      className="top-dock-nav"
      role="navigation"
      aria-label="Module navigation dock"
      onKeyDown={handleKeyDown}
    >
      <div ref={itemsRef} className="top-dock-nav-items">
        {visibleModules.map((module) => (
          <TopDockNavItem
            key={module.id}
            module={module}
            isActive={isActive(module.route)}
          />
        ))}
      </div>
      
      {overflowModules.length > 0 && (
        <>
          <button
            className="top-dock-more-btn"
            onClick={toggleOffcanvas}
            aria-label="Show more modules"
            aria-expanded={offcanvasOpen}
            type="button"
          >
            <i className="ri-more-line" aria-hidden="true"></i>
          </button>
          <TopDockOffcanvas
            isOpen={offcanvasOpen}
            toggle={toggleOffcanvas}
            modules={overflowModules}
          />
        </>
      )}
    </nav>
  );
};

export default TopDockNav;

